import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Select,
  Table,
  Space,
  Typography,
  Spin,
  message,
  Row,
  Col,
  Input,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useLazyGetReportsQuery } from "../store/api/summaryApi";
import {
  useLazyGetFilteredProductsQuery,
} from "../store/api/productApi";
import { useLazyGetOEMsQuery } from "../store/api/oemApi";
import { useLazyGetCategoriesQuery } from "../store/api/categoryApi";
import { useLazyGetClientsQuery } from "../store/api/clientApi";
import { useLazyGetUsersQuery } from "../store/api/userApi";
import type {
  ProductResponse,
  OEMResponse,
  CategoryResponse,
  ClientResponse,
  UserResponse,
} from "@OrianaTypes";
import type {
  ProductReportItemResponse,
  ProductReportRequest,
} from "../store/api/summaryApi";

const { Title } = Typography;

type ReportType = "product" | "oem" | "category" | "client" | "salesPerson" | "purchaseOrder";

interface SearchOption {
  value: number;
  label: string;
}

const SummaryReports: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // API hooks
  const [getReports, { data: reportData, isLoading: isLoadingReports }] =
    useLazyGetReportsQuery();
  const [getProducts] = useLazyGetFilteredProductsQuery();
  const [getOEMs] = useLazyGetOEMsQuery();
  const [getCategories] = useLazyGetCategoriesQuery();
  const [getClients] = useLazyGetClientsQuery();
  const [getUsers] = useLazyGetUsersQuery();

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle report type change
  const handleReportTypeChange = (value: ReportType) => {
    setReportType(value);
    setSearchValue("");
    setSelectedId(null);
    setPurchaseOrderId("");
    setSearchOptions([]);
  };

  // Debounced search function
  const performSearch = useCallback(
    async (searchTerm: string, type: ReportType) => {
      if (searchTerm.length < 3) {
        setSearchOptions([]);
        return;
      }

      setIsSearching(true);
      try {
        let options: SearchOption[] = [];

        switch (type) {
          case "product":
            const productsResult = await getProducts({
              searchKey: "productName",
              searchTerm: searchTerm,
              isActive: true,
              limit: 50,
            }).unwrap();
            options = productsResult.map((product: ProductResponse) => ({
              value: product.productId,
              label: product.productName,
            }));
            break;

          case "oem":
            const oemsResult = await getOEMs({
              searchKey: "oemName",
              searchTerm: searchTerm,
              isActive: true,
              limit: 50,
            }).unwrap();
            options = oemsResult.data.map((oem: OEMResponse) => ({
              value: oem.oemId,
              label: oem.name,
            }));
            break;

          case "category":
            const categoriesResult = await getCategories({
              searchKey: "categoryName",
              searchTerm: searchTerm,
              isActive: true,
              limit: 50,
            }).unwrap();
            options = categoriesResult.data.map((category: CategoryResponse) => ({
              value: category.categoryId,
              label: category.categoryName,
            }));
            break;

          case "client":
            const clientsResult = await getClients({
              searchKey: "clientName",
              searchTerm: searchTerm,
              isActive: true,
              limit: 50,
            }).unwrap();
            options = clientsResult.data.map((client: ClientResponse) => ({
              value: client.clientId,
              label: client.clientName,
            }));
            break;

          case "salesPerson":
            const usersResult = await getUsers({
              searchKey: "username",
              searchTerm: searchTerm,
              limit: 50,
            }).unwrap();
            options = usersResult.data.map((user: UserResponse) => ({
              value: user.userId,
              label: user.username,
            }));
            break;
        }

        setSearchOptions(options);
      } catch (error) {
        console.error("Error searching:", error);
        message.error("Failed to search. Please try again.");
      } finally {
        setIsSearching(false);
      }
    },
    [getProducts, getOEMs, getCategories, getClients, getUsers]
  );

  // Handle search input change with debounce
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setSelectedId(null);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounce (500ms)
    debounceTimerRef.current = setTimeout(() => {
      if (reportType && value.length >= 3) {
        performSearch(value, reportType);
      } else {
        setSearchOptions([]);
      }
    }, 500);
  };

  // Handle selection from dropdown
  const handleSelect = (value: number) => {
    setSelectedId(value);
    setSearchValue(
      searchOptions.find((opt) => opt.value === value)?.label || ""
    );

    // Build request params based on report type
    const params: ProductReportRequest = {};

    switch (reportType) {
      case "product":
        params.productId = value;
        break;
      case "oem":
        params.oemId = value;
        break;
      case "category":
        params.categoryId = value;
        break;
      case "client":
        params.clientId = value;
        break;
      case "salesPerson":
        params.salesPersonId = value;
        break;
    }

    // Fetch reports
    getReports(params);
  };

  // Handle PO ID input Enter key press
  const handlePOIdKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && purchaseOrderId.trim()) {
      const params: ProductReportRequest = {
        purchaseOrderId: purchaseOrderId.trim(),
      };
      getReports(params);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Table columns
  const columns: ColumnsType<ProductReportItemResponse> = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Category Name",
      dataIndex: "categoryName",
      key: "categoryName",
    },
    {
      title: "OEM Name",
      dataIndex: "oemName",
      key: "oemName",
    },
    {
      title: "Total Quantity",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: "Total Spare Qty",
      dataIndex: "totalSpareQuantity",
      key: "totalSpareQuantity",
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: "Avg Price/Unit",
      dataIndex: "avgPricePerUnit",
      key: "avgPricePerUnit",
      render: (value: number) => `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (value: number) => `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: "Final Price",
      dataIndex: "finalPrice",
      key: "finalPrice",
      render: (value: number) => `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Summary Reports</Title>

      <Card style={{ marginBottom: "24px" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text strong>Report Type</Typography.Text>
              <Select
                placeholder="Select report type"
                style={{ width: "100%" }}
                value={reportType}
                onChange={handleReportTypeChange}
                options={[
                  { value: "product", label: "Product" },
                  { value: "oem", label: "OEM" },
                  { value: "category", label: "Category" },
                  { value: "client", label: "Client" },
                  { value: "salesPerson", label: "Sales Person" },
                  { value: "purchaseOrder", label: "Purchase Order ID" },
                ]}
              />
            </Space>
          </Col>

          {reportType && (
            <Col xs={24} sm={12} md={16}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Typography.Text strong>
                  {reportType === "salesPerson"
                    ? "Search Sales Person"
                    : reportType === "purchaseOrder"
                    ? "Enter Purchase Order ID"
                    : `Search ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`}
                </Typography.Text>
                {reportType === "purchaseOrder" ? (
                  <Input
                    placeholder="Enter PO ID and press Enter (e.g., OSG-2024-25-00000001)"
                    style={{ width: "100%" }}
                    value={purchaseOrderId}
                    onChange={(e) => setPurchaseOrderId(e.target.value)}
                    onPressEnter={handlePOIdKeyPress}
                    allowClear
                  />
                ) : (
                  <Select
                    showSearch
                    placeholder={`Type at least 3 characters to search ${reportType}`}
                    style={{ width: "100%" }}
                    value={selectedId}
                    onSearch={handleSearchChange}
                    onChange={handleSelect}
                    options={searchOptions}
                    loading={isSearching}
                    notFoundContent={
                      isSearching ? (
                        <Spin size="small" />
                      ) : searchValue.length < 3 ? (
                        "Type at least 3 characters to search"
                      ) : (
                        "No results found"
                      )
                    }
                    filterOption={false}
                  />
                )}
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {isLoadingReports && (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      )}

      {reportData && reportData.data.length > 0 && (
        <Card>
          <Table
            columns={columns}
            dataSource={reportData.data}
            rowKey="productId"
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </Card>
      )}

      {reportData && reportData.data.length === 0 && !isLoadingReports && selectedId && (
        <Card>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Typography.Text type="secondary">
              No data found for the selected {reportType}.
            </Typography.Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SummaryReports;
